import React, { useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import {
  useClaimLabPod,
  useLabPool,
  useLabPools,
  useStockLabPool,
} from '../../hooks/lab-hooks';
import { LabPool, LabPoolSnapshot } from '../../support/labService';
import LabPoolAssignModal from './LabPoolAssignModal';
import {
  LabCard,
  LabDate,
  LabJsonView,
  LabMessage,
  LabPanelState,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';

export type LabPoolsPanelProps = {
  /** Stocking and assigning. The backend re-checks stewardship; this only hides buttons. */
  canSteward?: boolean;
};

const poolSentiment = (pool: LabPool) => {
  if (pool.state === 'closed' || pool.state === 'archived') return 'warning' as const;
  if (pool.is_open) return 'success' as const;
  return 'info' as const;
};

/**
 * Session pools - the pods available for an event, and who has one.
 *
 * `is_open` is the backend's answer, not `state === 'open'`: a pool can be marked open and
 * still be shut because its window has not started or has passed. Claiming honours that, so
 * the claim button follows `is_open` rather than the state tag.
 *
 * Stocking mirrors dCloud sessions into the pool. It does not create sessions, so a pool that
 * stays empty after stocking means the dCloud side has nothing matching the pool's demo.
 */
const LabPoolsPanel: React.FC<LabPoolsPanelProps> = ({ canSteward = false }) => {
  const [includeAll, setIncludeAll] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<LabPool | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);

  const { data: pools, isLoading, error, refetch } = useLabPools(includeAll);
  const { data: selected, isLoading: loadingSelected } = useLabPool(selectedSlug || undefined);
  const stock = useStockLabPool();
  const claim = useClaimLabPod();

  const handleStock = async (pool: LabPool) => {
    setActionError(null);
    setActionNote(null);

    try {
      const result = await stock.mutateAsync(pool.slug);
      const added = result?.added ?? 0;
      const alreadyPresent = result?.already_present ?? 0;
      setActionNote(
        `${pool.name}: ${added} pod${added === 1 ? '' : 's'} added, ${alreadyPresent} already in the pool.` +
          (added === 0 && alreadyPresent === 0
            ? ' Nothing matched the pool’s demo filter on the dCloud side.'
            : ''),
      );
    } catch (exc) {
      setActionError(labErrorMessage(exc, 'Could not stock the pool.'));
    }
  };

  const handleClaim = async (pool: LabPool) => {
    setActionError(null);
    setActionNote(null);

    try {
      const assignment = await claim.mutateAsync({ slug: pool.slug });
      setActionNote(
        `Claimed ${assignment.pod_label || assignment.session_key}. It is under your assignments.`,
      );
    } catch (exc) {
      setActionError(labErrorMessage(exc, 'Could not claim a pod.'));
    }
  };

  return (
    <>
      <LabCard
        title="Session pools"
        subtitle="Pods grouped for an event. A pool hands out sessions; it does not create them."
        actions={
          <>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeAll}
                onChange={(evt) => setIncludeAll(evt.target.checked)}
              />
              <span className="text-gray-700">Include closed</span>
            </label>
            <StbButton role="default" size="small" onClick={async () => refetch()}>
              Refresh
            </StbButton>
          </>
        }
      >
        {actionNote && <LabMessage>{actionNote}</LabMessage>}
        {actionError && <LabMessage tone="warning">{actionError}</LabMessage>}

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!pools?.length}
          emptyMessage={
            includeAll ? 'No pools exist yet.' : 'No open pools. Tick "include closed" to see the rest.'
          }
          loadingLabel="Loading pools..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Pool</th>
                  <th className="px-3 py-2 font-semibold">State</th>
                  <th className="px-3 py-2 font-semibold">Pods</th>
                  <th className="px-3 py-2 font-semibold">Window</th>
                  <th className="px-3 py-2 font-semibold">Contact</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {(pools || []).map((pool) => {
                  // Absent on a list row the backend did not compute counts for.
                  const snapshot: Partial<LabPoolSnapshot> = pool.snapshot || {};
                  const total = snapshot.members_total ?? (pool.members || []).length;
                  const usable = snapshot.members_usable ?? total;
                  const free = snapshot.members_free ?? 0;
                  const canClaim =
                    pool.is_open &&
                    (pool.claim_mode === 'self_claim' || pool.claim_mode === 'both');

                  return (
                    <tr key={pool.id} className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{pool.name}</div>
                        <div className="font-mono text-xs text-gray-500">{pool.slug}</div>
                        {pool.demo_name && (
                          <div className="text-xs text-gray-500">{pool.demo_name}</div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <LabStatusTag label={pool.state} sentiment={poolSentiment(pool)} />
                        {!pool.is_open && pool.state === 'open' && (
                          <div className="text-xs text-amber-700">
                            Outside its window, so claims are refused.
                          </div>
                        )}
                        <div className="text-xs text-gray-500">
                          {pool.allocation_mode === 'shared' ? 'Shared pods' : 'One pod each'}
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div>{`${free} free of ${total}`}</div>
                        <div className="text-xs text-gray-500">
                          {`${snapshot.assignments_live ?? 0} in use`}
                        </div>
                        {total === 0 && (
                          <div className="text-xs text-amber-700">Nothing stocked yet.</div>
                        )}
                        {total > 0 && usable < total && (
                          // A pod whose dCloud session has ended is still a member row.
                          <div className="text-xs text-amber-700">
                            {`${total - usable} pod${total - usable === 1 ? '' : 's'} not live`}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        <div>
                          {'Opens '}
                          <LabDate value={pool.opens_at} fallback="whenever" />
                        </div>
                        <div>
                          {'Closes '}
                          <LabDate value={pool.closes_at} fallback="never" />
                        </div>
                      </td>

                      <td className="px-3 py-2 text-xs text-gray-600">
                        {pool.contact_email || '—'}
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <StbButton
                            role="default"
                            size="xsmall"
                            onClick={async () =>
                              setSelectedSlug(selectedSlug === pool.slug ? null : pool.slug)
                            }
                          >
                            {selectedSlug === pool.slug ? 'Hide pods' : 'Pods'}
                          </StbButton>

                          {canClaim && (
                            <StbButton
                              role="request"
                              size="xsmall"
                              loading={claim.isPending}
                              onClick={async () => handleClaim(pool)}
                            >
                              Claim one
                            </StbButton>
                          )}

                          {canSteward && (
                            <>
                              <StbButton
                                role="update"
                                size="xsmall"
                                loading={stock.isPending}
                                onClick={async () => handleStock(pool)}
                              >
                                Stock
                              </StbButton>
                              <StbButton
                                role="add"
                                size="xsmall"
                                onClick={async () => {
                                  setSelectedSlug(pool.slug);
                                  setAssigning(pool);
                                }}
                              >
                                Assign
                              </StbButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      {selectedSlug && (
        <LabCard
          title={`Pods in ${selected?.name || selectedSlug}`}
          subtitle={selected?.description || undefined}
          actions={
            <StbButton role="cancel" size="small" onClick={async () => setSelectedSlug(null)}>
              Close
            </StbButton>
          }
        >
          <LabPanelState
            loading={loadingSelected}
            empty={!selected?.members?.length}
            emptyMessage="No pods in this pool. Stocking mirrors matching dCloud sessions into it."
            loadingLabel="Loading pods..."
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-3 py-2 font-semibold">Pod</th>
                    <th className="px-3 py-2 font-semibold">Session</th>
                    <th className="px-3 py-2 font-semibold">Assigned</th>
                    <th className="px-3 py-2 font-semibold">Held</th>
                    <th className="px-3 py-2 font-semibold">Notes</th>
                  </tr>
                </thead>

                <tbody>
                  {(selected?.members || []).map((member) => (
                    <tr key={member.id} className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{member.label || `Pod ${member.order}`}</div>
                        {!member.available && (
                          <LabStatusTag label="full" sentiment="warning" />
                        )}
                      </td>

                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">{member.session_key}</div>
                        <div className="text-xs text-gray-500">{member.session_state}</div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {`${member.assignment_count}/${member.max_assignees}`}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {member.reserved_for_email ? (
                          <>
                            <div>{member.reserved_for_email}</div>
                            <div>
                              {'until '}
                              <LabDate value={member.reserved_until} />
                            </div>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>

                      <td className="px-3 py-2 text-xs text-gray-600">{member.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </LabPanelState>

          {selected?.snapshot && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs uppercase tracking-wide text-gray-500">
                Snapshot
              </summary>
              <LabJsonView value={selected.snapshot} />
            </details>
          )}
        </LabCard>
      )}

      <LabPoolAssignModal pool={assigning} onClose={() => setAssigning(null)} />
    </>
  );
};

export default LabPoolsPanel;
